import type { LOCATION_SEARCH_TYPE } from '@/types'
import { useEffect } from 'react'
import type { Control, UseFormClearErrors, UseFormTrigger, UseFormWatch } from 'react-hook-form'
import { useWatch } from 'react-hook-form'
import type { HOTEL_FORM_STATE_TYPE } from '../types'

type UseHotelFormProps = {
  control: Control<HOTEL_FORM_STATE_TYPE>
  watch: UseFormWatch<HOTEL_FORM_STATE_TYPE>
  clearErrors: UseFormClearErrors<HOTEL_FORM_STATE_TYPE>
  trigger: UseFormTrigger<HOTEL_FORM_STATE_TYPE>
  initialValues: HOTEL_FORM_STATE_TYPE
  setLocation: (location: LOCATION_SEARCH_TYPE) => void
  updateFormValues?: (values: HOTEL_FORM_STATE_TYPE) => void
}

// Hook that attaches react-hook-form watch elements to each of the needed submitted values.
//  This tracks when specific values change to give more granular control over rerenders.
const useHotelFormSubmitValues = ({
  control,
  watch,
  clearErrors,
  trigger,
  initialValues,
  setLocation,
  updateFormValues,
}: UseHotelFormProps) => {
  // Watch all form fields and prepop or populate with initialValues
  const watchTripType = useWatch({
    control,
    name: 'tripType',
    defaultValue: initialValues.tripType,
  })

  const watchEndLocation = useWatch({
    control,
    name: 'endLocation',
    defaultValue: initialValues?.endLocation,
  })

  const watchStartLocation = useWatch({
    control,
    name: 'startLocation',
    defaultValue: initialValues?.startLocation,
  })

  const watchStartDate = useWatch({
    control,
    name: 'startDate',
    defaultValue: initialValues?.startDate,
  })

  const watchEndDate = useWatch({
    control,
    name: 'endDate',
    defaultValue: initialValues?.endDate,
  })

  const watchTravelers = useWatch({
    control,
    name: 'travelers',
    defaultValue: initialValues?.travelers,
  })

  // Form effects - watch subscription for updateFormValues
  useEffect(() => {
    const subscription = watch((value) => {
      if (updateFormValues) {
        updateFormValues(value as HOTEL_FORM_STATE_TYPE)
      }
    })
    return () => subscription.unsubscribe()
  }, [watch, setLocation, updateFormValues])

  // Update parent location state when end location changes (for SEM headlines)
  useEffect(() => {
    if (watchEndLocation) {
      setLocation(watchEndLocation)
    }
  }, [watchEndLocation, setLocation])

  // Clear errors when locations change
  useEffect(() => {
    clearErrors('startLocation')
  }, [clearErrors, watchStartLocation])

  useEffect(() => {
    clearErrors('endLocation')
  }, [clearErrors, watchEndLocation])

  // Clear errors when dates change
  useEffect(() => {
    clearErrors('startDate')
  }, [clearErrors, watchStartDate, watchTripType])

  useEffect(() => {
    clearErrors('endDate')
  }, [clearErrors, watchEndDate, watchTripType])

  // Trigger validation when travelers or trip type changes
  useEffect(() => {
    if (watchTravelers) {
      void trigger('travelers.children')
      void trigger('travelers.rooms')
    }
  }, [trigger, watchTripType, watchTravelers])

  return {
    watchTripType,
    watchEndLocation,
    watchStartLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers,
  }
}

export default useHotelFormSubmitValues
